import { useFetcher } from '@remix-run/react'
import { Fragment, useEffect, useRef, useState } from 'react'
import { getEncargaUnCocheUrl } from '#app/shared/lib/external-links.ts'
import { cn } from '#app/shared/lib/misc.tsx'
import CarPriceCard from '#app/shared/ui/data-display/carDescription/CarPriceCard.js'
import { Spacer } from '#app/shared/ui/data-display/Spacer.js'
import Spinner from '#app/shared/ui/utils/spinner.js'
import { Banner } from './Banner'
import LinkCard from './LinkCard'

type CarItem = { id: string } & Record<string, any>

type LoaderFilters = Record<string, string[]>

type PaginateResponse = { cars: CarItem[] }

interface InfiniteScrollProps {
  initialCars: CarItem[]
  totalCount: number
  filters: LoaderFilters
  sortField: string
  className?: string
}

const POSITIONS_FOR_LINK_CARDS = [5, 13] as const
const BATCH_SIZES = [11, 10] as const
const PAGINATE_ENDPOINT = '/api/cars/paginate'
// Triggers fetch when the sentinel is still ~1 row (~440px) away from viewport,
// i.e. roughly when the 3rd row of the latest batch starts to be visible.
const TRIGGER_ROOT_MARGIN = '440px 0px'

// Se construye en cada render porque la URL de "Encarga un coche" (app
// externa) depende de `ENV`, que no existe al importar el módulo.
const getLinkCardProps = () =>
  [
    {
      key: 'link-5',
      image: '/img/LinkCards/LinkCard.png',
      title: '¿No encuentras tu coche ideal?',
      description: 'Lo buscamos para ti.',
      buttonText: 'Encargar coche',
      buttonLink: getEncargaUnCocheUrl(),
      footerText: 'Del mundo a la puerta de tu casa.',
    },
    {
      key: 'link-14',
      image: '/img/LinkCards/LinkCard2.png',
      title: 'Aceptamos tu coche como parte del pago',
      description: '',
      buttonLink: '/vende-tu-coche',
      buttonText: 'Tasar coche',
      footerText: 'La mejor tasación del mercado.',
    },
  ] as const

function buildEnhancedItems(cars: CarItem[]) {
  const items: Array<{ key: string; node: React.ReactNode }> = []

  const lastLinkCardCarIndex = (() => {
    const positions = cars
      .map((_, i) => i)
      .filter((i) =>
        POSITIONS_FOR_LINK_CARDS.includes(
          (i % 14) as (typeof POSITIONS_FOR_LINK_CARDS)[number],
        ),
      )
    return positions[positions.length - 1] ?? -1
  })()

  cars.forEach((car, index) => {
    const pagePosition = index % 14
    const isLinkCardPosition = POSITIONS_FOR_LINK_CARDS.includes(
      pagePosition as (typeof POSITIONS_FOR_LINK_CARDS)[number],
    )

    if (isLinkCardPosition) {
      const linkCardIndex = POSITIONS_FOR_LINK_CARDS.indexOf(
        pagePosition as (typeof POSITIONS_FOR_LINK_CARDS)[number],
      )
      const linkCard = getLinkCardProps()[linkCardIndex]

      if (linkCard) {
        items.push({
          key: `${linkCard.key}-${index}`,
          node: (
            <LinkCard
              image={linkCard.image}
              title={linkCard.title}
              description={linkCard.description}
              buttonText={linkCard.buttonText}
              buttonLink={linkCard.buttonLink}
              footerText={linkCard.footerText}
              withFrame={false}
            />
          ),
        })
      }
    }

    items.push({
      key: car.id,
      node: <CarPriceCard car={car as any} inCochesUsados={true} />,
    })

    if (
      isLinkCardPosition &&
      pagePosition === 13 &&
      index === lastLinkCardCarIndex
    ) {
      items.push({
        key: `banner-${index}`,
        node: (
          <div className="w-full">
            <Banner />
          </div>
        ),
      })
    }
  })

  return items
}

export function InfiniteScroll({
  initialCars,
  totalCount,
  filters,
  sortField,
  className,
}: InfiniteScrollProps) {
  const fetcher = useFetcher<PaginateResponse>()

  const [cars, setCars] = useState<CarItem[]>(initialCars)
  const [batchesLoaded, setBatchesLoaded] = useState<number>(1)
  const triggerRef = useRef<HTMLDivElement | null>(null)
  const lastRequestedExcludeCount = useRef<number>(0)

  // Reset when loader data changes (filters/sort)
  useEffect(() => {
    setCars(initialCars)
    setBatchesLoaded(1)
    lastRequestedExcludeCount.current = 0
  }, [initialCars])

  // Append fetched cars when fetcher returns data
  useEffect(() => {
    if (fetcher.state !== 'idle') return
    const data = fetcher.data
    if (!data?.cars || data.cars.length === 0) return
    setCars((prev) => {
      const existingIds = new Set(prev.map((c) => c.id))
      const incoming = data.cars.filter((c) => !existingIds.has(c.id))
      if (incoming.length === 0) return prev
      return [...prev, ...incoming]
    })
    setBatchesLoaded((n) => n + 1)
  }, [fetcher.state, fetcher.data])

  const hasMore = cars.length < totalCount
  const isLoading = fetcher.state !== 'idle'

  const enhancedItems = buildEnhancedItems(cars)

  useEffect(() => {
    if (!hasMore) return
    const el = triggerRef.current
    if (!el) return

    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue
          if (fetcher.state !== 'idle') return
          if (cars.length >= totalCount) return
          if (lastRequestedExcludeCount.current === cars.length) return

          const nextSize =
            BATCH_SIZES[batchesLoaded % BATCH_SIZES.length] ?? BATCH_SIZES[0]
          const remaining = totalCount - cars.length
          const limit = Math.min(nextSize, remaining)
          if (limit <= 0) return

          lastRequestedExcludeCount.current = cars.length
          fetcher.submit(
            {
              filters,
              sortField,
              excludeIds: cars.map((c) => c.id),
              limit,
            },
            {
              method: 'post',
              action: PAGINATE_ENDPOINT,
              encType: 'application/json',
            },
          )
          return
        }
      },
      { threshold: 0, rootMargin: TRIGGER_ROOT_MARGIN },
    )

    observer.observe(el)
    return () => observer.disconnect()
  }, [hasMore, cars, totalCount, batchesLoaded, filters, sortField, fetcher])

  return (
    <>
      <div
        className={cn(
          className,
          `
            mt-1 flex auto-rows-[420px] flex-wrap justify-center gap-4

            lg:grid-cols-4

            md:grid-cols-3

            sm:grid-cols-2
          `,
        )}
      >
        {enhancedItems.map((item) => (
          <Fragment key={item.key}>{item.node}</Fragment>
        ))}
      </div>
      {hasMore && <div ref={triggerRef} className="h-1 w-full" />}
      <Spacer size="16px" />
      {isLoading && hasMore && (
        <div className="flex flex-col items-center justify-center space-y-3">
          <Spinner size={10} color="text-gray-200" />
        </div>
      )}
    </>
  )
}
